import React, { Fragment } from "react";

import Box from "@mui/material/Box";
import SwipeableDrawer from "@mui/material/SwipeableDrawer";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import ShareIcon from "@mui/icons-material/Share";
import LogoutIcon from "@mui/icons-material/Logout";

import useStepsDrawer from "./useStepsDrawer";

const StepsDrawer = props => {
  const {
    open,
    customProps,
    menu,
    selectedChild,
    isCobLoading,
    hasFormValidationErrors,
    handleChildClick,
    handleShareCaseClick,
    handleQuitSessionClick,
    toggleDrawer,
  } = useStepsDrawer(props);

  const OptionsList = () => (
    <Box
      role="presentation"
      sx={{
        width: "100%",
        height: "90%",
        overflow: "auto",
        p: 0,
        mt: {xs: 8.2, md: 7.25},
        mb: 1
      }}
    >
      <List sx={{ width: "100%", p: 0 }}>
        {menu.map((view, index) => (
          <Fragment key={view.name}>
            <ListItem disablePadding>
              <ListItemButton
                disableRipple
                sx={[
                  {
                    flexDirection: "row",
                    width: "100%",
                    alignItems: "flex-start",
                    color: "button.main",
                    pl: 2,
                    py: 0,
                    mt: 2,
                    cursor: "default"
                  }
                ]}
              >
                <ListItemText
                  primary={view.label}
                  primaryTypographyProps={{
                    variant: "StepsDrawer",
                    sx: {
                      fontSize: "0.72em !important",
                      alignItems: "flex-start",
                      color: "adviceMenu.sectionHeader",
                      textTransform: "uppercase"
                    }
                  }}
                  sx={{ alignItems: "flex-start" }}
                />
              </ListItemButton>
            </ListItem>
            {view?.children &&
              view?.children.length > 0 &&
              (() => {
                return (
                  <Collapse
                    in={true}
                    timeout="auto"
                    sx={{ width: "100%", p: 0 }}
                    unmountOnExit
                  >
                    <List component="div" sx={{ width: "100%", p: 0 }}>
                      {view?.children.map((child, index) => (
                        <ListItem key={index} disablePadding>
                          <ListItemButton
                            disableRipple
                            sx={[
                              {
                                flexDirection: "row",
                                width: "100%",
                                alignItems: "flex-start",
                                padding: "3px 12px 4px 12px",
                                color: "common.white",
                                backgroundColor:
                                  selectedChild === child.name
                                    ? "adviceMenu.selectedStepBg"
                                    : "transparent",
                                borderLeft: "4px solid ",
                                borderColor:
                                  selectedChild === child.name
                                  ? "adviceMenu.selectedStepBorder"
                                  : "transparent",
                                opacity: isCobLoading ? 0.8 : 1,
                                "&:hover": {
                                  backgroundColor: "adviceMenu.selectedStepBg",
                                  cursor: isCobLoading ? "wait" : "pointer"
                                }
                              }
                            ]}
                            onClick={handleChildClick(child, index)}
                          >
                            <ListItemIcon
                              variant="active"
                              sx={{
                                minWidth: 20,
                                alignItems: "flex-start",
                                "& .MuiSvgIcon-root": {
                                  mt: 0.45,
                                  fontSize: "1rem",
                                  color:
                                    child.hasBeenSeen && child.isFulfilled
                                      ? "adviceMenu.stepCompleted"
                                      : (child.hasBeenSeen ||
                                          hasFormValidationErrors) &&
                                        !child.isFulfilled
                                      ? "adviceMenu.stepWithError"
                                      : "adviceMenu.stepNotFilled"
                                }
                              }}
                            >
                              <CheckCircleOutlinedIcon />
                            </ListItemIcon>

                            <ListItemText
                              primary={child.label}
                              primaryTypographyProps={{
                                variant: "StepsDrawer",
                                sx: {
                                  fontSize: "0.93em !important"
                                }
                              }}
                              sx={{ alignItems: "start" }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Collapse>
                );
              })()}
          </Fragment>
        ))}
      </List>
    </Box>
  );

  const FooterOptions = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-end",
        width: "100%",
        height: "78px"
      }}
    >
      <List sx={{ width: "100%", backgroundColor: "primary.dark", pb: 2 }}>
        <ListItem disablePadding>
          <ListItemButton
            disableRipple
            sx={[
              {
                flexDirection: "row",
                width: "100%",
                alignItems: "flex-start",
                color: "common.white",
                backgroundColor: "primary.dark",
                px: 2,
                m: 0,
                py: 0.65,
                opacity: isCobLoading ? 0.8 : 1,
                "&:hover": {
                  color: "grey.400",
                  "& .MuiSvgIcon-root": { color: "grey.400" },
                  cursor: isCobLoading ? "wait" : "pointer"
                }
              }
            ]}
            onClick={handleShareCaseClick}
          >
            <ListItemIcon
              variant="active"
              sx={{
                minWidth: 20,
                alignItems: "flex-start",
                "& .MuiSvgIcon-root": {
                  mt: 0.2,
                  fontSize: "1rem"
                }
              }}
            >
              <ShareIcon />
            </ListItemIcon>
            <ListItemText
              primary="Dela ärende"
              primaryTypographyProps={{
                variant: "StepsDrawer"
              }}
              sx={{ alignItems: "flex-start", fontSize: "0.85em !important" }}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            disableRipple
            sx={[
              {
                flexDirection: "row",
                width: "100%",
                alignItems: "flex-start",
                color: "common.white",
                backgroundColor: "primary.dark",
                px: 2,
                m: 0,
                py: 0.2,
                opacity: isCobLoading ? 0.8 : 1,
                "&:hover": {
                  color: "grey.400",
                  "& .MuiSvgIcon-root": { color: "grey.400" },
                  cursor: isCobLoading ? "wait" : "pointer"
                }
              }
            ]}
            onClick={handleQuitSessionClick}
          >
            <ListItemIcon
              variant="active"
              sx={{
                minWidth: 20,
                alignItems: "flex-start",
                "& .MuiSvgIcon-root": {
                  mt: 0.2,
                  fontSize: "0.9rem"
                }
              }}
            >
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText
              primary="Avsluta rådgivning"
              primaryTypographyProps={{
                variant: "StepsDrawer"
              }}
              sx={{ alignItems: "flex-start", fontSize: "0.85em !important" }}
            />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <SwipeableDrawer
      PaperProps={{
        sx: {
          position: { md: "relative" },
          width: { xs: "65%", md: "100%" },
          // top: { xs: "75px", md: "50px" },
          overflow: "auto",
          borderRightColor: "gray.400",
          backgroundColor: "primary.main",
          zIndex: 999
        }
      }}
      anchor="left"
      open={open}
      onClose={toggleDrawer()}
      onOpen={toggleDrawer()}
      sx={{
        height: "100%",
        position: "fixed",
        left: 0,
        width: { md: "250px", lg: "270px" },
        zIndex: 999
      }}
      {...customProps}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          // justifyContent: "space-between",
          width: "100%",
          height: "100%",
          overflow: "hidden"
        }}
      >
        <OptionsList />
        <FooterOptions />
      </Box>
    </SwipeableDrawer>
  );
};

export default StepsDrawer;
